import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { addProject, updateProject, getProjectById } from "@/lib/portfolio-store";
import { ArrowLeft, Upload, X } from "lucide-react";

const AdminProjectForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: "",
    cat: "Site",
    tags: "",
    img: "",
    url: "",
    description: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [uploadMethod, setUploadMethod] = useState("url"); // "url" ou "file"

  useEffect(() => {
    if (isEditing) {
      const project = getProjectById(id);
      if (project) {
        setFormData({
          title: project.title,
          cat: project.cat,
          tags: project.tags.join(", "),
          img: project.img,
          url: project.url,
          description: project.description,
        });
        setImagePreview(project.img);
      }
    }
  }, [id, isEditing]);

  const categories = ["Site", "E-commerce", "Sistema", "App"];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Título é obrigatório";
    if (!formData.img.trim()) newErrors.img = "Imagem é obrigatória";
    if (!formData.description.trim())
      newErrors.description = "Descrição é obrigatória";
    if (!formData.url.trim()) newErrors.url = "URL é obrigatória";
    if (!formData.tags.trim()) newErrors.tags = "Tags são obrigatórias";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setTimeout(() => {
      const projectData = {
        title: formData.title.trim(),
        cat: formData.cat,
        tags: formData.tags
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t),
        img: formData.img.trim(),
        url: formData.url.trim(),
        description: formData.description.trim(),
      };

      try {
        if (isEditing) {
          updateProject(id, projectData);
        } else {
          addProject(projectData);
        }
        setLoading(false);
        navigate("/admin");
      } catch (error) {
        console.error("Erro ao guardar projeto:", error);
        setErrors({ img: "Erro ao guardar. Imagem pode ser muito grande. Tente uma URL externa." });
        setLoading(false);
      }
    }, 300);
  };

  const handleImageChange = (e) => {
    const url = e.target.value;
    setFormData({ ...formData, img: url });
    if (url) {
      setImagePreview(url);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tamanho (máx 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors({ ...errors, img: "Imagem deve ter menos de 5MB" });
      return;
    }

    // Validar tipo
    if (!file.type.startsWith("image/")) {
      setErrors({ ...errors, img: "Arquivo deve ser uma imagem" });
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result;
      const sizeMB = (base64.length / (1024 * 1024)).toFixed(2);

      // Warn if base64 is too large
      if (base64.length > 2 * 1024 * 1024) {
        setErrors({ ...errors, img: `Imagem comprimida é muito grande (${sizeMB}MB). Tente uma imagem menor.` });
        return;
      }

      setFormData({ ...formData, img: base64 });
      setImagePreview(base64);
      setErrors({ ...errors, img: undefined });
      console.log(`Imagem carregada: ${sizeMB}MB`);
    };
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setFormData({ ...formData, img: "" });
    setImagePreview("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) {
          handleFileUpload({ target: { files: [file] } });
        }
        break;
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f5ee]">
      {/* Header */}
      <header className="bg-[#0a0a0a] border-b-4 border-[#ff6b35]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-6">
          <button
            onClick={() => navigate("/admin")}
            className="flex items-center gap-2 font-display text-white hover:text-[#ff6b35] mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            Voltar
          </button>
          <h1 className="font-display text-3xl text-white">
            {isEditing ? "Editar Projeto" : "Novo Projeto"}
          </h1>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-12">
        <form onSubmit={handleSubmit} className="bg-white border-4 border-[#0a0a0a] p-8">
          {/* Título */}
          <div className="mb-8">
            <label className="block font-mono text-xs uppercase tracking-widest text-[#0a0a0a] mb-3">
              ✦ Título do Projeto
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="ex: Lumen Coffee Co."
              className="w-full brutal-input text-sm"
              disabled={loading}
            />
            {errors.title && (
              <p className="text-red-500 font-mono text-xs mt-2">{errors.title}</p>
            )}
          </div>

          {/* Categoria */}
          <div className="mb-8">
            <label className="block font-mono text-xs uppercase tracking-widest text-[#0a0a0a] mb-3">
              ✦ Categoria
            </label>
            <select
              value={formData.cat}
              onChange={(e) => setFormData({ ...formData, cat: e.target.value })}
              className="w-full brutal-input text-sm"
              disabled={loading}
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          {/* Descrição */}
          <div className="mb-8">
            <label className="block font-mono text-xs uppercase tracking-widest text-[#0a0a0a] mb-3">
              ✦ Descrição
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="ex: E-commerce de café especializado..."
              className="w-full brutal-input text-sm h-24 resize-none"
              disabled={loading}
            />
            {errors.description && (
              <p className="text-red-500 font-mono text-xs mt-2">
                {errors.description}
              </p>
            )}
          </div>

          {/* Tags */}
          <div className="mb-8">
            <label className="block font-mono text-xs uppercase tracking-widest text-[#0a0a0a] mb-3">
              ✦ Tags (separadas por vírgula)
            </label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="ex: React, Branding, Shopify"
              className="w-full brutal-input text-sm"
              disabled={loading}
            />
            {errors.tags && (
              <p className="text-red-500 font-mono text-xs mt-2">{errors.tags}</p>
            )}
          </div>

          {/* Imagem - Métodos de Upload */}
          <div className="mb-8">
            <label className="block font-mono text-xs uppercase tracking-widest text-[#0a0a0a] mb-3">
              ✦ Imagem do Projeto
            </label>

            {/* Tabs de método */}
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setUploadMethod("file")}
                className={`flex-1 p-3 border-3 font-mono text-xs uppercase tracking-widest transition-colors ${
                  uploadMethod === "file"
                    ? "bg-[#ff6b35] text-[#0a0a0a] border-[#0a0a0a]"
                    : "bg-white text-[#0a0a0a] border-[#0a0a0a]"
                }`}
              >
                <Upload size={14} className="inline mr-2" />
                Upload de Ficheiro
              </button>
              <button
                type="button"
                onClick={() => setUploadMethod("url")}
                className={`flex-1 p-3 border-3 font-mono text-xs uppercase tracking-widest transition-colors ${
                  uploadMethod === "url"
                    ? "bg-[#ff6b35] text-[#0a0a0a] border-[#0a0a0a]"
                    : "bg-white text-[#0a0a0a] border-[#0a0a0a]"
                }`}
              >
                URL da Imagem
              </button>
            </div>

            {/* Upload por URL */}
            {uploadMethod === "url" && (
              <div>
                <input
                  type="text"
                  value={formData.img}
                  onChange={handleImageChange}
                  placeholder="ex: https://images.unsplash.com/..."
                  className="w-full brutal-input text-sm"
                  disabled={loading}
                />
                {errors.img && (
                  <p className="text-red-500 font-mono text-xs mt-2">{errors.img}</p>
                )}
              </div>
            )}

            {/* Upload por Ficheiro */}
            {uploadMethod === "file" && (
              <div>
                <div
                  tabIndex={0}
                  onPaste={handlePaste}
                  className="border-3 border-dashed border-[#0a0a0a] p-6 text-center cursor-pointer hover:bg-[#f8f5ee] transition-colors focus:outline-none focus:bg-[#f8f5ee]"
                >
                  <label className="block cursor-pointer">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={loading}
                    />
                    <div className="font-mono text-sm text-[#0a0a0a]">
                      <p className="font-bold mb-1">Clique para selecionar imagem</p>
                      <p className="text-xs text-[#0a0a0a]/70">
                        ou arraste aqui (máx 5MB)
                      </p>
                      <p className="text-xs text-[#ff6b35] font-bold mt-2">
                        ou cole com Ctrl+V
                      </p>
                    </div>
                  </label>
                </div>
                {errors.img && (
                  <p className="text-red-500 font-mono text-xs mt-2">{errors.img}</p>
                )}
              </div>
            )}

            {/* Preview da Imagem */}
            {imagePreview && (
              <div className="mt-4 relative">
                <div className="relative h-48 bg-[#0a0a0a] border-4 border-[#0a0a0a] overflow-hidden">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-full object-cover"
                    onError={() => setImagePreview("")}
                  />
                </div>
                <button
                  type="button"
                  onClick={clearImage}
                  className="absolute top-2 right-2 bg-red-500 text-white p-2 hover:bg-red-600 border-2 border-white"
                >
                  <X size={16} />
                </button>
                <p className="text-xs text-[#0a0a0a]/70 mt-2">
                  {uploadMethod === "file" ? "Ficheiro carregado com sucesso" : "Preview da URL"}
                </p>
              </div>
            )}
          </div>

          {/* URL do Projeto */}
          <div className="mb-8">
            <label className="block font-mono text-xs uppercase tracking-widest text-[#0a0a0a] mb-3">
              ✦ URL do Projeto
            </label>
            <input
              type="text"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              placeholder="ex: https://www.veenttovagos.com"
              className="w-full brutal-input text-sm"
              disabled={loading}
            />
            {errors.url && (
              <p className="text-red-500 font-mono text-xs mt-2">{errors.url}</p>
            )}
          </div>

          {/* Ações */}
          <div className="flex gap-4 pt-6 border-t-4 border-[#0a0a0a]">
            <button
              type="button"
              onClick={() => navigate("/admin")}
              disabled={loading}
              className="flex-1 btn-brutal disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 btn-brutal btn-brutal-orange disabled:opacity-50"
            >
              {loading ? "A guardar..." : isEditing ? "Atualizar" : "Criar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminProjectForm;
